import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../api/store";
import { ProposedDataState } from "./propose-data-state";
import { SubTypeOption } from "../../api/gms/group-category/group-category-api-state";
import { Affiliation, ClientBasicInfoState } from "../gms-proposal-forms/basic-information/basic-information.state";
import { createEmptyAffiliation } from "../gms-proposal-forms/basic-information/basic-information.slice";
import { BasicInfoContacts } from "../gms-proposal-forms/basic-information/basic-information.types";
import { EligibilityAge } from "../gms-proposal-forms/gc-tog-ea-mop/gc-tog-ea-mop-state";
import { initialRowsFor6670, initialRowsFor7175, initialRowsFor7680, optionForBorrowerAge, profileAgeRate, remainingAgeRate1865 } from "../gms-proposal-forms/age-bracket-and-benefit-amount/age-bracket-and-benefit-amount.initial_state";
import { AmountOfLoansInput } from "../gms-proposal-forms/amount-of-loans/amount-loans.state";
import { AddRiderLevelRankingInputPayload, initialInputs, InputByLevelRanking } from "../gms-proposal-forms/coverage/level-ranking/coverage-type-level-ranking.state";
import { initialRows } from "../gms-proposal-forms/coverage/salary-rank/coverage-type-by-salary-rank.slice";
import { ProposalSalaryRanking, RiderSalaryRankInputPayload, RowData } from "../gms-proposal-forms/coverage/salary-rank/coverage-type-by-salary-rank.state";
import { v4 as uuidv4 } from "uuid";
import { SetBorrowerAgeAmountFromApiPayload, SetBorrowerAgeFromApiPayload } from "../gms-proposal-forms/age-bracket-and-benefit-amount/age-bracket-and-benefit-amount.state";
import { SINGLE_RIDER_IDS_COVERAGE, SINGLE_RIDER_IDS_DISEASE, SINGLE_RIDER_IDS_GCI_DENGUE_MOSQUITO, SINGLE_RIDER_IDS_GCI_TOP4, SINGLE_RIDER_IDS_GCIR } from "../../constant/gms-constant";
import { RiderGHIRandGAMERRandBB } from "../gms-proposal-forms/attachable-rider/attachable-riders.state";
import { AddRiderUniformCoverageInputPayload } from "../gms-proposal-forms/coverage/uniform-coverage/coverage-type-uniform-coverage.state";

const initialState: ProposedDataState = {
    channelType: {
        selectedChannelBox: null,
        channelSubName: null,
        channelNumber: null,
        channelEmail: null,
    },
    selectProposalAs: null,

    // BASIC INFO
    basicInformation: {
        groupName: "",
        noOfLives: 0,
        companyMobileNo: "",
        companyTelephone: "",
        completeBusinessAddress: "",
        proposalAddress: "",
        email: "",
        addressDesignation: "",
        designation: "",
        contactPersonSalutation: "",
        contactPersonFirstName: "",
        contactPersonMiddleName: "",
        contactPersonLastName: "",
        contactNumber: "",
        contactTelephone: "",
        tinNumber: "",
        secNumber: "",
    },
    selectedNatureOfBusinessCategory: null,
    selectedNatureOfBusinessItem: null,
    companyNumber: null,
    companyMobileNumber: null,
    contactPersonMobileNumber: null,
    affiliation: [],

    //GC
    selectedGroupClassificationBox: null,
    otherSpecificationInputGC: null,
    // ToG
    selectedTypeOfGroupBox: null,
    otherSpecificationInputToG: null,
    selectedSubType: [],
    // EA
    eligibilityAge: [
        { label: "Minimum Age", age: null },
        { label: "Max Age", age: null },
    ],
    //MoP
    selectedFundingArrangements: null,
    othersNumberOfMonthsMoP: null,

    // proposal type (Customize/Prototype)
    selectedProposalType: null,
    // prototype
    selectedPrototypeType: null,
    // product
    selectedProductType: null,
    // basic plan
    selectedBasicPlans: null,
    // coverage type
    selectedCoverageType: null,
    // COVERAGE LEVEL RANKING
    inputsForLevelRanking: initialInputs,
    ridersForLevelRanking: [],
    // COVERAGE: UNIFORM
    selectedUniformCoverage: null,
    ridersForUniform: [],
    // COVERAGE: SALARY RANK
    rowsForSalaryRank: initialRows,
    ridersForSalaryRank: [],
    csvFile: null,
    existingCsvFilePath: null,

    // RIDERS
    selectedRiders: [],
    selectedGHIR: null,
    selectedGAMERR9: null,
    selectedGAMERR14: null,
    selectedBMSR: null,
    selectedGDR: null,
    selectedBMS: null,
    selectedGTPDRCR: null,

    // AMOUNT OF LOANS
    selectedAmountOfLoans: null,
    selectedAmountOfLoansInput: null,
    selectFAmountOfLoans: null,
    minAmount: null,
    maxAmount: null,
    loanPortfolio: null,
    ridersAmountOfLoans: [],

    // payment term
    selectedPaymentTerm: null,
    // loan maturity
    selectedLoanMaturity: null,
    selectedLoanMaturityMonths: null,
    // commission and services
    commissionValue: null,
    serviceValue: null,
    // notes
    content: '',
    // supporting files(GMS)
    files: [],

    // AGE BORROWER
    remainingAgeRate: remainingAgeRate1865,
    profileAgeRate: profileAgeRate,
    borrowerAgeOptions: optionForBorrowerAge,
    singleRateTableFor6670: initialRowsFor6670,
    singleRateTableFor7175: initialRowsFor7175,
    singleRateTableFor7680: initialRowsFor7680,
    selected6670: null,
    selected7175: null,
    selected7680: null,


    isPrototype: false,
};


const proposedDataSlice = createSlice({
    name: 'proposedDataSlice',
    initialState,
    reducers: {
        pa_updateChannelAndSubName: ( state, action: PayloadAction<{  selectedChannelBox: SubTypeOption | null;  channelSubName: string | null;  channelSubNumber: string | null,  channelSubEmail: string | null}>  ) => {
            state.channelType.selectedChannelBox = action.payload.selectedChannelBox;
            state.channelType.channelSubName = action.payload.channelSubName;
            state.channelType.channelNumber = action.payload.channelSubNumber;
            state.channelType.channelEmail = action.payload.channelSubEmail;
        },
        pa_setProposalType: (state, action: PayloadAction<SubTypeOption>) => {
            state.selectProposalAs = action.payload;
        },

        // BASIC INFO
        pa_setForm: ( state, action: PayloadAction<ClientBasicInfoState> ) => {
            state.basicInformation = action.payload;
        },
        pa_setCompanyNumber: (state, action: PayloadAction<{type: BasicInfoContacts; value: SubTypeOption | null}>) => {
            const { type, value} = action.payload
            if(type === 'comNumber'){
                state.companyNumber = value;
            }
            if(type === 'comContactNumber'){
                state.companyMobileNumber = value;
            }
            if(type === 'contactPersonNumber'){
                state.contactPersonMobileNumber = value;
            }
        },
        pa_setNatureOfBusiness: (state, action: PayloadAction<{ value: SubTypeOption, type: 'category' | 'item'}>) => {
            const { value, type } = action.payload;
            if(type === 'category') {
                state.selectedNatureOfBusinessCategory = value;
            }
            if(type === 'item') {
                state.selectedNatureOfBusinessItem = value;
            }
        },
        pa_populateAffiliation: (state, action: PayloadAction<Affiliation[]>) => {
            state.affiliation = action.payload.length > 0 ? action.payload : [createEmptyAffiliation()];
        },

        // GC
        pa_setGroupClassification: (state, action: PayloadAction<SubTypeOption>) => {
            state.selectedGroupClassificationBox = action.payload;
            if (action.payload.id !== 5) {
                state.otherSpecificationInputGC = "";
            }
        },
        pa_setOtherSpecification: (state, action: PayloadAction<string | null>) => {
            state.otherSpecificationInputGC = action.payload;
        },

        // ToG
        pa_setTypeOfGroup: (state, action: PayloadAction<SubTypeOption>) => {
            state.selectedTypeOfGroupBox = action.payload;
            state.selectedSubType = [];
            if (action.payload.id !== 7) {
                state.otherSpecificationInputToG = "";
            }
        },
        pa_setSelectedSubTypesFromAPI: (state, action: PayloadAction<SubTypeOption[]>) => {
            state.selectedSubType = action.payload;
        },
        pa_setOtherSpecificationToG: (state, action: PayloadAction<string | null>) => {
            state.otherSpecificationInputToG = action.payload;
        },

        // EA
        pa_setAgeByLabel: ( state, action: PayloadAction<{ label: EligibilityAge["label"]; age: number | null }> ) => {
            const item = state.eligibilityAge.find(i => i.label === action.payload.label);
            if (item) {
                // Allow null (for empty input)
                if (action.payload.age === null) {
                    item.age = null;
                    return;
                }
                // Enforce max 3 digits
                const age = Math.floor(action.payload.age); // ensure integer
                if (age >= 0 && age <= 65) {
                    item.age = age;
                }
            }
        },

        // MoP
        pa_setSelectedFundingArrangementsBox: (state, action: PayloadAction<SubTypeOption | null>) => {
            state.selectedFundingArrangements = action.payload;
        },

        // proposal type (Customize/Prototype)
        pa_setSelectedProposalTypeBox: (state, action: PayloadAction<SubTypeOption | null>) => {
            state.selectedProposalType = action.payload;
        },
        // prototype
        pa_setSelectedPrototypeTypeBox: (state, action: PayloadAction<SubTypeOption | null>) => {
            state.selectedPrototypeType = action.payload;
        },
        // product
        pa_setSelectedProductTypeBox: (state, action: PayloadAction<SubTypeOption | null>) => {
            state.selectedProductType = action.payload;
        },
        // basic plan
        pa_setSelectedBasicPlansBox: (state, action: PayloadAction<SubTypeOption | null>) => {
            state.selectedBasicPlans = action.payload;
        },

        // COVERAGE
        pa_setSelectedCoverageTypeBox: (state, action: PayloadAction<SubTypeOption | null>) => {
            state.selectedCoverageType = action.payload;
        },
        // COVERAGE: LEVEL RANKING
        pa_setLevelRankingFromAPI: ( state, action: PayloadAction<{ id: string, label: string; fAmount: number }[]>) => {
            state.inputsForLevelRanking = action.payload;
        },
        pa_addRiderLevelRankingInput: (state, action: PayloadAction<AddRiderLevelRankingInputPayload>) => {
            const { id, label, inputs, values = [], riderValues = []} = action.payload;
            const riderId = Number(id);

            const singleSelectGCIR: number[] = SINGLE_RIDER_IDS_GCIR;
            const singleSelectGroup: number[] = SINGLE_RIDER_IDS_COVERAGE;
            const singleSelectDisease: number[] = SINGLE_RIDER_IDS_DISEASE; // 13,16,19

            const singleSelectGCI_TOP4: number[] = SINGLE_RIDER_IDS_GCI_TOP4;
            const singleSelectGCI_DENGUE_MOSQUITO: number[] = SINGLE_RIDER_IDS_GCI_DENGUE_MOSQUITO;

            // If rider belongs to single select group
            if (singleSelectGroup.includes(id)) {
                state.ridersForLevelRanking = state.ridersForLevelRanking?.filter(
                    r => !singleSelectGroup.includes(r.id)
                ) || [];
            }

            if (singleSelectGCIR.includes(id)) {
                state.ridersForLevelRanking = state.ridersForLevelRanking?.filter(
                    r => !singleSelectGCIR.includes(r.id)
                ) || [];
            }

            if (singleSelectDisease.includes(id)) {
                state.ridersForLevelRanking = state.ridersForLevelRanking?.filter(
                    r => !singleSelectDisease.includes(r.id)
                ) || [];
            }
            // GCI
            if (singleSelectGCI_TOP4.includes(id)) {
                state.ridersForLevelRanking = state.ridersForLevelRanking?.filter(
                    r => !singleSelectGCI_TOP4.includes(r.id)
                ) || [];
            }
            if (singleSelectGCI_DENGUE_MOSQUITO.includes(id)) {
                state.ridersForLevelRanking = state.ridersForLevelRanking?.filter(
                    r => !singleSelectGCI_DENGUE_MOSQUITO.includes(r.id)
                ) || [];
            }

            const exists = state.ridersForLevelRanking?.some(r => r.id === id);
            if (exists) return;

            let finalRows: InputByLevelRanking[] = [];

            if(inputs && inputs.length > 0) {
                finalRows = inputs.map(v => {
                    const baseRow = state.inputsForLevelRanking.find(r => r.label === v.label);

                    return {
                        id: baseRow?.id ?? uuidv4(),
                        fAmount: v.fAmount,
                        label: baseRow?.label ?? v.label,
                    }
                });
            } else {
                finalRows = state.inputsForLevelRanking.map(row => ({
                    ...row,
                    id: row.id ?? uuidv4(),
                }));
            }

            state.ridersForLevelRanking?.push({
                id,
                label,
                inputs: finalRows
            });
        },
        // COVERAGE: UNIFORM
        pa_setSelectedUniformCoverage: (state, action: PayloadAction<{ label: string; value: string | number; }>) => {
            let rawValue = String(action.payload.value).replace(/[^0-9.]/g, "");
            const parts = rawValue.split(".");
            if (parts.length > 2) {
                rawValue = parts[0] + "." + parts[1];
            }
            const numericValue = Number(rawValue) || 0; 
            state.selectedUniformCoverage = {
                label: action.payload.label,
                fAmount: numericValue,
            };
            state.ridersForUniform.forEach(rider => {
                rider.rider.fAmount = numericValue;
            });
        },
        pa_addRiderUniformCoverageInput: (state, action: PayloadAction<AddRiderUniformCoverageInputPayload[]>) => {
            const selectedLabel = state.selectedUniformCoverage?.label ?? "";
            state.ridersForUniform = action.payload.map(r => ({
                ...r,
                rider: {
                    ...r.rider,
                    label: selectedLabel 
                }
            }));
        },
        // COVERAGE: SALARY RANK
        pa_setSalaryRankFromAPI: ( state, action: PayloadAction<ProposalSalaryRanking[]> ) => {
            
            const apiData = action.payload;
            state.rowsForSalaryRank = [];
            
            apiData.forEach((item) => {
                const multiplier = Number(item.salary_multiplier.replace("X", ""));
                
                state.rowsForSalaryRank.push({
                    id: uuidv4(),
                    multiplierValue: multiplier,
                    multiplierLabel: `X${multiplier}`,
                    salary: item.amount,
                    designation: item.designation,
                    result: item.amount * multiplier,
                });
            });
        },
        pa_addRiderSalaryRankInput: (state, action: PayloadAction<RiderSalaryRankInputPayload>) => {
            const { id, label, values = [], riderValues = [], type = 'checkbox'} = action.payload;
            
            const singleSelectGroup: number[] = SINGLE_RIDER_IDS_COVERAGE;
            const singleSelectGCIR: number[] = SINGLE_RIDER_IDS_GCIR;
            const singleSelectDisease: number[] = SINGLE_RIDER_IDS_DISEASE;

            const singleSelectGCI_TOP4: number[] = SINGLE_RIDER_IDS_GCI_TOP4;
            const singleSelectGCI_DENGUE_MOSQUITO: number[] = SINGLE_RIDER_IDS_GCI_DENGUE_MOSQUITO;

            if (singleSelectGroup.includes(id)) {
                state.ridersForSalaryRank = state.ridersForSalaryRank?.filter(
                    r => !singleSelectGroup.includes(r.id)
                ) || [];
            }

            if (singleSelectGCIR.includes(id)) {
                state.ridersForSalaryRank = state.ridersForSalaryRank?.filter(
                    r => !singleSelectGCIR.includes(r.id)
                ) || [];
            }

            if (singleSelectDisease.includes(id)) {
                state.ridersForSalaryRank = state.ridersForSalaryRank?.filter(
                    r => !singleSelectDisease.includes(r.id)
                ) || [];
            }
            // GCI
            if (singleSelectGCI_TOP4.includes(id)) {
                state.ridersForSalaryRank = state.ridersForSalaryRank?.filter(
                    r => !singleSelectGCI_TOP4.includes(r.id)
                ) || [];
            }
            if (singleSelectGCI_DENGUE_MOSQUITO.includes(id)) {
                state.ridersForSalaryRank = state.ridersForSalaryRank?.filter(
                    r => !singleSelectGCI_DENGUE_MOSQUITO.includes(r.id)
                ) || [];
            }

            const exists = state.ridersForSalaryRank.some(r => r.id === id);
            if (exists) return;

            let finalRows: RowData[] = [];
            const hasApiValues = values.length > 0;
            const hasCheckboxValues = riderValues.length > 0;

            switch (type) {
                case "api":
                    finalRows = values.map(v => {
                        const baseRow = state.rowsForSalaryRank.find(r => r.designation === v.designation);

                        return {
                            id: baseRow?.id ?? uuidv4(),
                            multiplierLabel: baseRow?.multiplierLabel ?? "",
                            multiplierValue: baseRow?.multiplierValue ?? 0,
                            salary: v.amount ?? 0,
                            designation: v.designation,
                            result: v.amount,
                            checked: false,
                        };
                    });
                    break;

                case "checkbox":
                    finalRows = riderValues.map(v => ({
                        id: v.id ?? uuidv4(),
                        multiplierLabel: v.multiplierLabel,
                        multiplierValue: v.multiplierValue,
                        salary: v.salary,
                        designation: v.designation,
                        result: v.result,
                        checked: false,
                    }));
                    break;

                default:
                    finalRows = state.rowsForSalaryRank.map(row => ({
                        ...row,
                        id: row.id ?? uuidv4(),
                    }));
                break;
            }

            state.ridersForSalaryRank.push({
                id,
                label,
                rider: finalRows
            });
        },
        // RIDERS
        pa_setSelectedRiders(state, action: PayloadAction<{ id: number; label: string; inputs?: any[] }[]>) {
            state.selectedRiders = action.payload;
        },
        pa_toggleRider(state, action: PayloadAction<{ id: number; label: string }>) {
            const rider = action.payload;

            const exists = state.selectedRiders.some(r => r.id === rider.id);

            if (exists) {
                // Remove rider if it exists
                state.selectedRiders = state.selectedRiders.filter(r => r.id !== rider.id);
                return;
            }

            const singleSelectGroup: number[] = SINGLE_RIDER_IDS_COVERAGE;
            const singleSelectGCIR: number[] = SINGLE_RIDER_IDS_GCIR;
            const singleSelectDisease: number[] = SINGLE_RIDER_IDS_DISEASE;

            const singleSelectGCI_TOP4: number[] = SINGLE_RIDER_IDS_GCI_TOP4;
            const singleSelectGCI_DENGUE_MOSQUITO: number[] = SINGLE_RIDER_IDS_GCI_DENGUE_MOSQUITO;

            if (singleSelectGroup.includes(rider.id)) {
                state.selectedRiders = [
                    ...state.selectedRiders.filter(r => !singleSelectGroup.includes(r.id)),
                    { id: rider.id, label: rider.label }
                ];
            } else if (singleSelectGCIR.includes(rider.id)) {
                state.selectedRiders = [
                    ...state.selectedRiders.filter(r => !singleSelectGCIR.includes(r.id)),
                    { id: rider.id, label: rider.label }
                ];
            } else if (singleSelectDisease.includes(rider.id)) {
                state.selectedRiders = [
                    ...state.selectedRiders.filter(r => !singleSelectDisease.includes(r.id)),
                    { id: rider.id, label: rider.label }
                ];
            } else if (singleSelectGCI_TOP4.includes(rider.id)) {
                state.selectedRiders = [
                    ...state.selectedRiders.filter(r => !singleSelectGCI_TOP4.includes(r.id)),
                    { id: rider.id, label: rider.label }
                ];
            } else if (singleSelectGCI_DENGUE_MOSQUITO.includes(rider.id)) {
                state.selectedRiders = [
                    ...state.selectedRiders.filter(r => !singleSelectGCI_DENGUE_MOSQUITO.includes(r.id)),
                    { id: rider.id, label: rider.label }
                ];
            } else {
                state.selectedRiders.push({
                    id: rider.id,
                    label: rider.label
                });
            }
        },
        pa_setBMSR(state, action: PayloadAction<RiderGHIRandGAMERRandBB | null>) {
            state.selectedBMSR = action.payload;
        },

        // AMOUNT OF LOANS
        pa_setSelectedAmountOfLoansBox: (state, action: PayloadAction<SubTypeOption | null>) => {
            state.selectedAmountOfLoans = action.payload;
        },
        pa_setSelectedAmountOfLoansInputFromAPI: (state, action: PayloadAction<AmountOfLoansInput | null>) => {
            state.selectedAmountOfLoansInput = action.payload;
        },
        pa_setFAmountOfLoans: (state, action: PayloadAction<number | null>) => {
            state.selectFAmountOfLoans = action.payload;
        },
        pa_updateAmountOfLoansRiderFAmount: (state, action: PayloadAction<{ riderId: number; fAmount: string | number }>) => {
            const { riderId, fAmount } = action.payload;
            const riderItem = state.ridersAmountOfLoans.find(r => r.id === riderId);
            if (!riderItem) return;
            riderItem.rider.fAmount = fAmount;
        },
        pa_addAmountOfLoansRider: (state, action: PayloadAction<{ id: number; label: string }>) => {
            if (!state.selectedAmountOfLoans || !state.selectedAmountOfLoansInput) return;

            const { id, label } = action.payload;
            const exists = state.ridersAmountOfLoans.some(r => r.id === id);
            if (exists) return;

            state.ridersAmountOfLoans.push({
                id,
                label,
                rider: {
                    label: state.selectedAmountOfLoans.name,
                    fAmount: state.selectedAmountOfLoansInput.fAmount,
                },
            });
        },
        pa_setUpdateAmount: (state, action: PayloadAction<{type: 'min' | 'max' | 'port', amount: number | null}>) => {
            const { type, amount} = action.payload;
            if(type === 'min'){
                state.minAmount = amount;
                return
            }
            if(type === 'max'){
                state.maxAmount = amount;
                return
            }
            if(type === 'port'){
                state.loanPortfolio = amount;
                return
            }
        },
        // NOTE OF CFE
        pa_setNotesContent: (state, action: PayloadAction<string>) => {
            state.content = action.payload;
        },
        // CSV FILE (MASTER FILE)
        pa_setExistingCsvFilePath: (state, action: PayloadAction<string | null>) => {
            state.existingCsvFilePath = action.payload;
        },

        // PAYMENT TERMS
        pa_setSelectedPaymentTermBox: (state, action: PayloadAction<SubTypeOption | null>) => {
            state.selectedPaymentTerm = action.payload;
        },
        pa_setUpdateLoanMaturityMonths: (state, action: PayloadAction<number>) => {
            state.selectedLoanMaturityMonths = action.payload;
        },
        

        // BORROWER
        pa_addBorrowerAgeOption: (state, action: PayloadAction<{id: number; label: string, typeOfAge: string, amount?: number | null, type: 'rm' | 'ap'}>) => {
            const { id, label, type, amount, typeOfAge } = action.payload;
            
            if (type === "rm") {
                const item = state.remainingAgeRate.find(rate => rate.id === id);
                if (item) {
                    item.label = label;
                    item.typeOfAge = typeOfAge;
                    item.isShow = label.trim() !== "";
                    
                    // Only update amount if it was passed
                    if (amount !== undefined) {
                        item.amount = amount;
                    }
                }
            }
            if(type === 'ap') {
                state.profileAgeRate.label = label
            }
            
        },
        pa_setBorrowerAgeIsShow: (state, action: PayloadAction<{ id: number, isShow: boolean}>) => {
            const { id, isShow } = action.payload;
            const option = state.borrowerAgeOptions.find(item => item.id === id);

            if (option) {
                option.isShow = isShow
            }
        },
        pa_setBorrowerAgeFromAPI: (state, action: PayloadAction<{value?: SetBorrowerAgeFromApiPayload, fAmount?: SetBorrowerAgeAmountFromApiPayload,  type: 'check' | 'amount'}>) => {
            const { value, fAmount, type } = action.payload;

            state.borrowerAgeOptions = state.borrowerAgeOptions.map((option) => {

                switch (option.id) {

                    case 3:
                        return {
                            ...option,
                            ...(type === 'check' && value
                                ? { value: value.borrower_age_66_70 }
                                : {}
                            ),
                            ...(type === 'amount' && fAmount
                                ? { amount: fAmount.borrower_amount_66_70 }
                                : {}
                            )
                        };

                    case 4:
                        return {
                            ...option,
                            ...(type === 'check' && value
                                ? { value: value.borrower_age_71_75 }
                                : {}
                            ),
                            ...(type === 'amount' && fAmount
                                ? { amount: fAmount.borrower_amount_71_75 }
                                : {}
                            )
                        };

                    case 5:
                        return {
                            ...option,
                            ...(type === 'check' && value
                                ? { value: value.borrower_age_76_80 }
                                : {}
                            ),
                            ...(type === 'amount' && fAmount
                                ? { amount: fAmount.borrower_amount_76_80 }
                                : {}
                            )
                        };

                    default:
                        return option;
                }

            });
        },
        pa_updateBorrowerAgeValue: ( state, action: PayloadAction<{ id: number; value: boolean; amount?: number | null; }> ) => {
            const { id, value, amount } = action.payload;

            const item = state.remainingAgeRate.find(
                (rate) => rate.id === id
            );

            if (item) {
                item.value = value;
                // Only update amount if a value was provided
                if (amount !== undefined) {
                    item.amount = amount;
                }
            }
        },

        // SUPPORTING FILE OF CFE
        pa_populateSupportingDocuments: (state, action: PayloadAction<{ fileName: string, filePath: string, supportingId: null | number}[] | null>) => {
            state.files = (action.payload ?? []).map((item, index) => ({
                id: index,
                file: null,
                fileName: item.fileName,
                filePath: item.filePath,
                type: "old",
                supportingId: item.supportingId,
            }));
        },

        pa_setIsPrototype: (state, action: PayloadAction<boolean>) => {
            state.isPrototype = action.payload;
        },

        // commission and services
        pa_setCommissionValue: (state, action: PayloadAction<number | null>) => {
            state.commissionValue = action.payload;
        },
        pa_setServiceValue: (state, action: PayloadAction<number | null>) => {
            state.serviceValue = action.payload;
        },

        pa_clearProposeData: () => initialState,
    },
});

export const {
    pa_updateChannelAndSubName,
    pa_setProposalType,

    // BASIC INFORMATION
    pa_setForm,
    pa_setCompanyNumber,
    pa_setNatureOfBusiness,
    pa_populateAffiliation,

    // GROUP CLASSIFICATION
    pa_setGroupClassification,
    pa_setOtherSpecification,

    // TYPE OF GROUP
    pa_setTypeOfGroup,
    pa_setSelectedSubTypesFromAPI,
    pa_setOtherSpecificationToG,

    // ELIGIBILITY AGE
    pa_setAgeByLabel,

    // MODE OF PAYMENT
    pa_setSelectedFundingArrangementsBox,

    // PROPOSAL TYPE
    pa_setSelectedProposalTypeBox,
    pa_setSelectedPrototypeTypeBox,
    pa_setSelectedProductTypeBox,
    pa_setSelectedBasicPlansBox,

    // COVERAGE
    pa_setSelectedCoverageTypeBox,
    pa_setLevelRankingFromAPI,
    pa_addRiderLevelRankingInput,
    pa_setSelectedUniformCoverage,
    pa_addRiderUniformCoverageInput,
    pa_setSalaryRankFromAPI,
    pa_addRiderSalaryRankInput,

    // RIDERS
    pa_setSelectedRiders,
    pa_toggleRider,
    pa_setBMSR, // SPECIAL RIDER

    // AMOUNT OF LOANS
    pa_setSelectedAmountOfLoansBox,
    pa_setSelectedAmountOfLoansInputFromAPI,
    pa_setFAmountOfLoans,
    pa_updateAmountOfLoansRiderFAmount,
    pa_addAmountOfLoansRider,
    pa_setUpdateAmount,

    // PAYMENT TERMS
    pa_setSelectedPaymentTermBox,
    pa_setUpdateLoanMaturityMonths,

    // BORROWER AGE
    pa_addBorrowerAgeOption,
    pa_setBorrowerAgeIsShow,
    pa_setBorrowerAgeFromAPI,
    pa_updateBorrowerAgeValue,

    // COMMISSION AND SERVICE
    pa_setCommissionValue,
    pa_setServiceValue,

    // NOTES
    pa_setNotesContent,

    // CSV
    pa_setExistingCsvFilePath,

    // SUPPORTING FILES
    pa_populateSupportingDocuments,

    // PROTOTYPE
    pa_setIsPrototype,

    // CLEAR
    pa_clearProposeData,
} = proposedDataSlice.actions;

export default proposedDataSlice.reducer;

// CHANNEL
export const selectProposeChanelType = (state: RootState) => state.proposedDataSlice.channelType;

// PROPOSAL AS
export const selectProposeProposalAs = (state: RootState) => state.proposedDataSlice.selectProposalAs;

// BASIC INFORMATION
export const selectProposeBasicInformation = (state: RootState) => state.proposedDataSlice.basicInformation;
export const selectProposeNatureOfBusinessCategory = (state: RootState) => state.proposedDataSlice.selectedNatureOfBusinessCategory;
export const selectProposeNatureOfBusinessItem = (state: RootState) => state.proposedDataSlice.selectedNatureOfBusinessItem;
export const selectProposeCompanyNumber = (state: RootState) => state.proposedDataSlice.companyNumber;
export const selectProposeCompanyMobileNumber = (state: RootState) => state.proposedDataSlice.companyMobileNumber;
export const selectProposeContactPersonMobileNumber = (state: RootState) => state.proposedDataSlice.contactPersonMobileNumber;
export const selectProposeAffiliation = (state: RootState) => state.proposedDataSlice.affiliation;

// GROUP CLASSIFICATION
export const selectProposeGroupClassification = (state: RootState) => state.proposedDataSlice.selectedGroupClassificationBox;
export const selectProposeOtherSpecificationGC = (state: RootState) => state.proposedDataSlice.otherSpecificationInputGC;

// TYPE OF GROUP
export const selectProposeTypeOfGroup = (state: RootState) => state.proposedDataSlice.selectedTypeOfGroupBox;
export const selectProposeOtherSpecificationToG = (state: RootState) => state.proposedDataSlice.otherSpecificationInputToG;
export const selectProposeSubType = (state: RootState) => state.proposedDataSlice.selectedSubType;

// ELIGIBILITY AGE
export const selectProposeEligibilityAge = (state: RootState) => state.proposedDataSlice.eligibilityAge;
export const selectProposeMinAge = (state: RootState) => {
    const minItem = state.proposedDataSlice.eligibilityAge.find(i => i.label === "Minimum Age");
    return minItem?.age ?? null;
};
export const selectProposeMaxAge = (state: RootState) => {
    const maxItem = state.proposedDataSlice.eligibilityAge.find(i => i.label === "Max Age");
    return maxItem?.age ?? null;
};
// MODE OF PAYMENT
export const selectProposeFundingArrangements = (state: RootState) => state.proposedDataSlice.selectedFundingArrangements;
export const selectProposeOthersNumberOfMonthsMoP = (state: RootState) => state.proposedDataSlice.othersNumberOfMonthsMoP;

// PROPOSAL TYPE
export const selectProposeProposalType = (state: RootState) => state.proposedDataSlice.selectedProposalType;
export const selectProposePrototypeType = (state: RootState) => state.proposedDataSlice.selectedPrototypeType;
export const selectProposeProductType = (state: RootState) => state.proposedDataSlice.selectedProductType;
export const selectProposeBasicPlans = (state: RootState) => state.proposedDataSlice.selectedBasicPlans;

// COVERAGE
export const selectProposeCoverageType = (state: RootState) => state.proposedDataSlice.selectedCoverageType;

// COVERAGE: LEVEL RANKING
export const selectProposeInputsForLevelRanking = (state: RootState) => state.proposedDataSlice.inputsForLevelRanking;
export const selectProposeRidersForLevelRanking = (state: RootState) => state.proposedDataSlice.ridersForLevelRanking;

// COVERAGE: UNIFORM
export const selectProposeSelectedUniformCoverage = (state: RootState) => state.proposedDataSlice.selectedUniformCoverage;
export const selectProposeRidersForUniform = (state: RootState) => state.proposedDataSlice.ridersForUniform;

// COVERAGE: SALARY RANK
export const selectProposeRowsForSalaryRank = (state: RootState) => state.proposedDataSlice.rowsForSalaryRank;
export const selectProposeRidersForSalaryRank = (state: RootState) => state.proposedDataSlice.ridersForSalaryRank;

// RIDERS
export const selectProposeSelectedRiders = (state: RootState) => state.proposedDataSlice.selectedRiders;
// SPECIAL RIDER: BURIAL
export const selectProposeSelectedBMSR = (state: RootState) => state.proposedDataSlice.selectedBMSR;

// AMOUNT OF LOANS
export const selectProposeSelectedAmountOfLoans = (state: RootState) => state.proposedDataSlice.selectedAmountOfLoans;
export const selectProposeSelectedAmountOfLoansInput = (state: RootState) => state.proposedDataSlice.selectedAmountOfLoansInput;
export const selectProposeFAmountOfLoans = (state: RootState) => state.proposedDataSlice.selectFAmountOfLoans;
export const selectProposeMinAmount = (state: RootState) => state.proposedDataSlice.minAmount;
export const selectProposeMaxAmount = (state: RootState) => state.proposedDataSlice.maxAmount;
export const selectProposeLoanPortfolio = (state: RootState) => state.proposedDataSlice.loanPortfolio;
export const selectProposeRidersAmountOfLoans = (state: RootState) => state.proposedDataSlice.ridersAmountOfLoans;

// PAYMENT TERMS
export const selectProposePaymentTerm = (state: RootState) => state.proposedDataSlice.selectedPaymentTerm;
export const selectProposeLoanMaturity = (state: RootState) => state.proposedDataSlice.selectedLoanMaturity;
export const selectProposeLoanMaturityMonths = (state: RootState) => state.proposedDataSlice.selectedLoanMaturityMonths;

// COMMISSION AND SERVICE
export const selectProposeCommissionValue = (state: RootState) => state.proposedDataSlice.commissionValue;
export const selectProposeServiceValue = (state: RootState) => state.proposedDataSlice.serviceValue;

// NOTES
export const selectProposeContent = (state: RootState) => state.proposedDataSlice.content;

// SUPPORTING FILES
export const selectProposeFiles = (state: RootState) => state.proposedDataSlice.files;

// CSV
export const selectProposeCSVFile = (state: RootState) => state.proposedDataSlice.csvFile;
export const selectProposeExistingCSVFilePath = (state: RootState) => state.proposedDataSlice.existingCsvFilePath;

// RATE / AGE
export const selectProposeRemainingAgeRate = (state: RootState) => state.proposedDataSlice.remainingAgeRate;
export const selectProposeProfileAgeRate = (state: RootState) => state.proposedDataSlice.profileAgeRate;

// BORROWER AGE
export const selectProposeBorrowerAgeOptions = (state: RootState) => state.proposedDataSlice.borrowerAgeOptions;
export const proposedVisibleRemainingAge = createSelector([selectProposeRemainingAgeRate],(remainingAgeRate) => remainingAgeRate.filter((item) => item.isShow),);
// RATE TABLES
export const selectProposeSingleRateTableFor6670 = (state: RootState) => state.proposedDataSlice.singleRateTableFor6670;
export const selectProposeSingleRateTableFor7175 = (state: RootState) => state.proposedDataSlice.singleRateTableFor7175;
export const selectProposeSingleRateTableFor7680 = (state: RootState) => state.proposedDataSlice.singleRateTableFor7680;

// SELECTED RATE TABLE VALUES
export const selectProposeSelected6670 = (state: RootState) => state.proposedDataSlice.selected6670;
export const selectProposeSelected7175 = (state: RootState) => state.proposedDataSlice.selected7175;
export const selectProposeSelected7680 = (state: RootState) => state.proposedDataSlice.selected7680;

// PROTOTYPE
export const selectProposeIsPrototype = (state: RootState) => state.proposedDataSlice.isPrototype;